import { useMemo, useState } from 'react'
import { Box, Typography, Skeleton, styled } from '@mui/material'
import { nothing } from '$func/utils'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { AppBarBackTitleSearch } from '$common/app-bar/app-bar-back-title-search'
import {
  ActionButton,
  ACTION_BUTTON_HEIGHT,
  ACTION_BUTTON_MARGIN_BOTTOM,
} from './action-button'
import { APP_BAR_HEIGHT } from '$common/app-bar/app-bar.constants'
import { useWindowHeight } from '$func/window.react-hook'
import { WithAppBarAndActionButtonProps } from './searchable-list-page.types'
import { useDebounce } from '$common/react-hooks/use-debounce'

const SUB_HEADER_TEXT_HEIGHT = 37
const ACTION_BUTTONS_BOTTOM_MARGIN = 10

const SubHeaderContainer = styled(Box)`
  background-color: white;
  box-sizing: border-box;
  padding: 10px 20px 2px 0;
`

export function WithAppBarAndActionButton({
  appBarTitle,
  loading = false,
  hideActionButton = false,
  subHeaderText = '',
  onBack,
  children,
  actionButtons,
  secondaryActionButtons,
  heightOffset = 0,
  FAB,
  isFABVisible,
  useCustomScroller,
  changeQueryDebounceMS = 1000,
}: WithAppBarAndActionButtonProps) {
  const windowHeight = useWindowHeight()
  const [query, setQuery] = useState<string>()

  const { debouncedValue: debouncedQuery } = useDebounce(
    query,
    changeQueryDebounceMS,
  )

  const toRenderActionButtons = useMemo(() => {
    if (!!query || !actionButtons || hideActionButton) return []
    return actionButtons.filter(({ text }) => !!text)
  }, [actionButtons])

  const toRenderSecondaryActionButtons = useMemo(() => {
    if (!!query || !secondaryActionButtons || hideActionButton) return []
    return secondaryActionButtons.filter(({ text }) => !!text)
  }, [secondaryActionButtons])

  const showSubHeaderText = !query && !!subHeaderText

  const listHeight =
    windowHeight -
    APP_BAR_HEIGHT -
    (toRenderActionButtons.length
      ? toRenderActionButtons.length * ACTION_BUTTON_HEIGHT +
        ACTION_BUTTON_MARGIN_BOTTOM
      : 0) -
    (toRenderSecondaryActionButtons.length
      ? toRenderSecondaryActionButtons.length * ACTION_BUTTON_HEIGHT +
        ACTION_BUTTON_MARGIN_BOTTOM
      : 0) -
    (subHeaderText ? SUB_HEADER_TEXT_HEIGHT : 0) -
    heightOffset

  return (
    <PageWithAppBar
      FAB={FAB}
      isFABVisible={isFABVisible}
      appBar={
        <AppBarBackTitleSearch
          title={appBarTitle}
          onBack={onBack}
          setQuery={setQuery}
        />
      }
      useCustomScroller={useCustomScroller}
    >
      {/* {(Boolean(query) && !hideActionButton && Boolean(actionButtonText) ) && (
        <ActionButton
          text={actionButtonText}
          icon={actionButtonIcon}
          onClick={onActionButtonClick}
        />
      )} */}
      {toRenderActionButtons?.length ? (
        <ActionButtonsContainer>
          {toRenderActionButtons.map((actionButton, index) => (
            <ActionButton
              text={actionButton.text}
              icon={actionButton.icon}
              onClick={actionButton.onClick || nothing}
              key={actionButton.text}
              isLast={actionButtons && actionButtons.length - 1 === index}
              onboardingType={actionButton.onboardingType}
            />
          ))}
        </ActionButtonsContainer>
      ) : null}
      {toRenderSecondaryActionButtons?.length ? (
        <ActionButtonsContainer>
          {toRenderSecondaryActionButtons.map((actionButton, index) => (
            <ActionButton
              text={actionButton.text}
              icon={actionButton.icon}
              onClick={actionButton.onClick || nothing}
              key={actionButton.text}
              isLast={actionButtons && actionButtons.length - 1 === index}
              onboardingType={actionButton.onboardingType}
            />
          ))}
        </ActionButtonsContainer>
      ) : null}
      {showSubHeaderText && (
        <SubHeaderContainer>
          {loading ? (
            <Skeleton variant="text" width={120} />
          ) : (
            <Typography color="primary" variant="subtitle3">
              {subHeaderText}
            </Typography>
          )}
        </SubHeaderContainer>
      )}

      {children(listHeight, debouncedQuery || '')}
    </PageWithAppBar>
  )
}

const ActionButtonsContainer = styled('div')`
  background-color: ${({ theme }) => theme.palette.neutral000};
  margin-bottom: ${ACTION_BUTTONS_BOTTOM_MARGIN}px;
  box-shadow: ${({ theme }) => theme.shadows[3]};
`
